import type { Channel, ChannelDTO } from '@entities/channel'
import type { Partner, PartnerDTO } from '@entities/partner/model/types'
import type { User, UserDTO } from '@entities/user/model/types'

export type PostState = 'draft' | 'planned' | 'published'

export interface Post {
    id: number
    title: string
    creator: Pick<User, 'username' | 'firstName' | 'lastName' | 'photoUrl'>
    state: PostState
    channels: number[]
    messages: Message[]
    publishDate?: Date
    autoDeleteHours?: number
    keepOnTopHours?: number
    isPinned?: boolean
    notify?: boolean
    createdAt: Date
    updatedAt: Date
    isShared: boolean
    isAdvertising: boolean
    advertisingInfo?: AdvertisingInfo
    postType: PostType
}

export interface PostDTO {
    id: number
    title: string
    creator: Pick<UserDTO, 'username' | 'first_name' | 'last_name' | 'photo_url'>
    state: PostState
    channels: number[]
    messages: MessageDTO[]
    publish_date?: string
    auto_delete_seconds?: number
    keep_on_top_seconds?: number
    is_pinned?: boolean
    notify?: boolean
    created_at: string
    updated_at: string
    is_shared: boolean
    is_advertising: boolean
    advertising_info?: AdvertisingInfoDTO
    custom_post_type: number
}

export interface AdvertisingInfo {
    adCampaign: number
    budget: number
    views: number
    subscribersChange: number
    sellerConfirm: boolean
    customerConfirm: boolean
    paymentConfirmed: boolean
    isEditing: boolean
    viewToken: string
    priceMethod: AdPriceMethod
    statistics: AdStatistics
    internetResource: Pick<Channel, 'id' | 'name' | 'username'>
    partner: Pick<Partner, 'id' | 'name' | 'tgUsername'>
    links?: AdLink[]
    ordInfo?: OrdInfo
    contractUuid?: string
}

export interface AdvertisingInfoDTO {
    ad_campaign: number
    budget: number
    views: number
    subscribers_change: number
    seller_confirm: boolean
    customer_confirm: boolean
    payment_confirmed: boolean
    is_editing: boolean
    view_token: string
    price_method: AdPriceMethod
    statistics: AdStatisticsDTO
    links?: AdLinkDTO[]
    ord_info?: OrdInfoDTO
    internet_resource: Pick<ChannelDTO, 'id' | 'name' | 'username'>
    partner: Pick<PartnerDTO, 'id' | 'name' | 'tg_username'>
    contract_uuid?: string
}

export type OrdResourceType = 'site' | 'app'

export interface OrdInfo {
    withAgent: boolean
    category: string
    agentCommission: number
    resourceName: string
    resourceType: OrdResourceType
    resourceUrl: string
    reallocations: Reallocation[]
    nds: boolean
    ndsPercent: number
}

export interface OrdInfoDTO {
    with_agent: boolean
    category: string
    agent_commission: number
    resource_name: string
    resource_type: OrdResourceType
    resource_url: string
    reallocations: Reallocation[]
    nds: boolean
    nds_percent: number
}

export interface Reallocation {
    amount: number
}

export interface AdLink {
    text: string
    href: string
    isUnique: string
    generatedHref: string
    clickCount: number
}

export interface AdLinkDTO {
    text: string
    href: string
    is_unique: string
    generated_href: string
    click_count: number
}

export interface AdStatistics {
    clicks: number
    joins: number
    leaves: number
}

export interface AdStatisticsDTO {
    clicks: number
    joins: number
    leaves: number
}

export enum AdPriceMethod {
    fix = 'fix',
    cpm = 'cpm'
}

export enum MessageType {
    TEXT = 'text',
    PHOTO = 'photo',
    VIDEO = 'video',
    DOCUMENT = 'document',
    AUDIO = 'audio',
    VOICE = 'voice-message',
    QUIZ = 'quiz',
    ALBUM = 'album'
}

export interface FileDTO {
    id: string
    file_id: string
    file_name: string
    file_status: string
    file_type: string
    file_url: string
    preview_url?: string
}

export interface UploadFile extends File {
    response: FileDTO
    thumbUrl?: string
}

export interface MessageDTO {
    id: number
    text: string
    show_caption_above_media?: boolean
    files: UploadFile[]
    message_type: MessageType
    quiz?: {
        options?: string[]
        question?: string
    }
    repost_link?: string
    reply_link?: string
}

export interface Message {
    id: number
    text: string
    messageType: MessageType
    file: UploadFile[]
    options?: string[]
    question?: string
    isCaptionAbove?: boolean
    repostLink?: string
    replyLink?: string
}

export interface PostType {
    id?: number
    name?: string
    color?: string
    value?: number
    channel?: number
}

export interface PostTypeDTO {
    id?: number
    name?: string
    color?: string
    value?: number
    channel?: number
}
